import React from "react";
import { useForm } from "react-hook-form";
import { CancelButton, FormButton } from "../../../components/Button";
import { FormFieldset, Input } from "../../../components/Form";
import Loader from "../../../components/Loader";
import useHiring from "./useHiring";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface IAddHiring {
  name: string;
  phone: string;
  email: string;
  education: string;
  english: string;
  experience: string;
  experienceTech: string;
  hrNote: string;
  status: string;
  currentCTC: string;
  expectedCTC: string;
  noticePeriod: string;
  source: string;
  linkedInId: string;
  location: string;
  resume?: FileList;
  raviNote: string;
  interviewDate: string;
  interviewTime: string;
  meetingLink: string;
  type: string;
  salaryBeforeLastIncrement: string;
  lastIncrement: string;
  reasonForJobChange: string;
}

const AddHiring: React.FC = () => {
  const navigate = useNavigate();
  const { loading } = useHiring();

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<IAddHiring>();

  const [resumeUrl, setResumeUrl] = React.useState<string | null>(null);

  const uploadResume = async (file: File) => {
    const formData = new FormData();
    formData.append("resume", file);

    try {
      const result = await ApiService({
        method: "POST",
        endpoint: `${apiPaths.uploadResume}`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = result?.resumeUrl || result?.fileUrl || result?.url;
      if (url) {
        setResumeUrl(url);
        return url;
      }
      return null;
    } catch (error) {
      toast.error("Resume upload failed");
      return null;
    }
  };

  const onSubmit = async (data: IAddHiring) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key !== "resume" && value !== undefined && value !== null && value !== "") {
        formData.append(key, value as string);
      }
    });
    if (resumeUrl) {
      formData.append("resume", resumeUrl); // Send the URL as 'resume'
    }
    try {
      await ApiService({
        method: "POST",
        endpoint: apiPaths.createHiring,
        data: formData,
      });
      setResumeUrl(resumeUrl)
      toast.success("Hiring entry added successfully");
      navigate("/hiring");
      reset();
      // Optionally, redirect or update UI
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Error adding hiring entry"
      );
    }
  };

  return (
    <div className="flex flex-col items-center w-full h-full pr-8 pl-6 ">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full ">
        <div className="border-b-2 w-[100%]">
          <h1 className="text-3xl mb-2 text-fontBlack ml-5">Add Hiring</h1>
        </div>
        {loading && <Loader />}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6 p-6 h-[75vh] overflow-y-scroll"
        >
          <FormFieldset Ftitle="Hiring Details">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 m-4">
              <Input
                label="Name"
                name="name"
                value={watch("name") || ""}
                onChange={(e) => setValue("name", e.target.value)}
              />
              <Input
                label="Phone"
                name="phone"
                value={watch("phone") || ""}
                onChange={(e) => setValue("phone", e.target.value)}
              />
              <Input
                label="Email"
                name="email"
                value={watch("email") || ""}
                onChange={(e) => setValue("email", e.target.value)}
              />
              <Input
                label="Education"
                name="education"
                value={watch("education") || ""}
                onChange={(e) => setValue("education", e.target.value)}
              />
              <Input
                label="Experience"
                name="experience"
                value={watch("experience") || ""}
                onChange={(e) => setValue("experience", e.target.value)}
              />
              <Input
                label="Experience Tech"
                name="experienceTech"
                value={watch("experienceTech") || ""}
                onChange={(e) => setValue("experienceTech", e.target.value)}
              />
              <Input
                label="HR Note"
                name="hrNote"
                value={watch("hrNote") || ""}
                onChange={(e) => setValue("hrNote", e.target.value)}
              />
              {/* Status Dropdown */}
              <div className="flex flex-col">
                <label className="text-fontBlack text-sm mb-2">Status</label>
                <select
                  value={watch("status") || ""}
                  onChange={e => setValue("status", e.target.value)}
                  className="border rounded p-2"
                >
                  <option value="">Select Status</option>
                  <option value="Follow-Up">Follow-Up</option>
                  <option value="Hold">Hold</option>
                  <option value="Schedule">Schedule</option>
                  <option value="Rejection">Rejection</option>
                  <option value="High Salary">High Salary</option>
                  <option value="HR Rejection">HR Rejection</option>
                  <option value="HR Action">HR Action</option>
                  <option value="TL Action">TL Action</option>
                  <option value="Call NA">Call NA</option>
                  <option value="Practical Needed">Practical Needed</option>
                </select>
              </div>
              {/* Type Dropdown */}
              <div className="flex flex-col">
                <label className="text-fontBlack text-sm mb-2">Type</label>
                <select
                  value={watch("type") || ""}
                  onChange={e => setValue("type", e.target.value)}
                  className="border rounded p-2"
                >
                  <option value="">Select Type</option>
                  <option value="Fresher">Fresher</option>
                  <option value="Experience">Experience</option>
                </select>
              </div>
              {/* English Dropdown */}
              <div className="flex flex-col">
                <label className="text-fontBlack text-sm mb-2">English</label>
                <select
                  value={watch("english") || ""}
                  onChange={e => setValue("english", e.target.value)}
                  className="border rounded p-2"
                >
                  <option value="">Select English Level</option>
                  <option value="Poor">Poor</option>
                  <option value="Good">Good</option>
                  <option value="Fluent">Fluent</option>
                </select>
              </div>
              <Input
                label="Current CTC"
                name="currentCTC"
                value={watch("currentCTC") || ""}
                onChange={(e) => setValue("currentCTC", e.target.value)}
              />
              <Input
                label="Expected CTC"
                name="expectedCTC"
                value={watch("expectedCTC") || ""}
                onChange={(e) => setValue("expectedCTC", e.target.value)}
              />
              <Input
                label="Notice Period"
                name="noticePeriod"
                value={watch("noticePeriod") || ""}
                onChange={(e) => setValue("noticePeriod", e.target.value)}
              />
              <Input
                label="Source"
                name="source"
                value={watch("source") || ""}
                onChange={(e) => setValue("source", e.target.value)}
              />
              <Input
                label="LinkedIn Id"
                name="linkedInId"
                value={watch("linkedInId") || ""}
                onChange={(e) => setValue("linkedInId", e.target.value)}
              />
              <Input
                label="Location"
                name="location"
                value={watch("location") || ""}
                onChange={(e) => setValue("location", e.target.value)}
              />
              <div className="flex flex-col">
                <label className="text-fontBlack text-sm mb-2">Resume</label>
                <input
                  type="file"
                  className="border rounded p-2"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const url = await uploadResume(file);
                      if (url) setResumeUrl(url);
                    }
                  }}
                />
              </div>
              <Input
                label="Ravi Note"
                name="raviNote"
                value={watch("raviNote") || ""}
                onChange={(e) => setValue("raviNote", e.target.value)}
              />
              <Input
                label="Interview Date"
                name="interviewDate"
                type="date"
                value={watch("interviewDate") || ""}
                onChange={(e) => setValue("interviewDate", e.target.value)}
              />
              <Input
                label="Interview Time"
                name="interviewTime"
                type="time"
                value={watch("interviewTime") || ""}
                onChange={(e) => setValue("interviewTime", e.target.value)}
              />
              <Input
                label="Meeting Link"
                name="meetingLink"
                value={watch("meetingLink") || ""}
                onChange={(e) => setValue("meetingLink", e.target.value)}
              />
              <Input
                label="Salary Before Last Increment"
                name="salaryBeforeLastIncrement"
                value={watch("salaryBeforeLastIncrement") || ""}
                onChange={(e) => setValue("salaryBeforeLastIncrement", e.target.value)}
              />
              <Input
                label="Last Increment"
                name="lastIncrement"
                value={watch("lastIncrement") || ""}
                onChange={(e) => setValue("lastIncrement", e.target.value)}
              />
              <Input
                label="Reason For Job Change"
                name="reasonForJobChange"
                value={watch("reasonForJobChange") || ""}
                onChange={(e) => setValue("reasonForJobChange", e.target.value)}
              />
            </div>
          </FormFieldset>
          <div className="flex justify-center mt-6 ml-2 gap-3">
            <CancelButton
              className="py-2 px-4 h-[56px] w-[200.5px]"
              onClick={() => navigate("/hiring")}
              children="Cancel"
              type="button"
            />
            <FormButton
              className="py-2 px-4  h-[56px] w-[200.5px]"
              type="submit"
              children="ADD"
            />
          </div>
        </form>
      </div>
    </div>
  );
};
export default AddHiring;
